import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AddRoadOutlined, HolidayVillageOutlined, PictureInPictureOutlined } from "@mui/icons-material";
import type { SvgIconProps } from "@mui/material";
import { Alert, AlertTitle, Box, Button, Typography } from "@mui/material";
import { Feature, FeatureCollection, MultiPolygon, Polygon } from "geojson";

import { LoadingButton, Table, TableBody, TableCell, TableHead, TableRow } from "ui";
import Progress, { Step } from "ui/Progress/Progress";

import { fondServiceErrorCodes, requestJson, useGetFeaturesQuery } from "fond/api";
import { useGetImportLayer } from "fond/hooks/useGetImportLayers";
import { FondServiceLayerIds } from "fond/layers";
import { addImport } from "fond/redux/imports";
import * as turf from "fond/turf";
import { ImportStatus, MuPowerInputLayerIds, StageStatus } from "fond/types";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { isImportInProgress } from "fond/utils/layers";
import { ConfirmModal, useStackedNavigationContext } from "fond/widgets";
import StackedNavigationHeader from "fond/widgets/StackedNavigation/StackedNavigationHeader";

interface ImportItem {
  name: string;
  icon: React.ComponentType<SvgIconProps>;
  type: string;
  iconColor?: SvgIconProps["color"];
}

const importItems: ImportItem[] = [
  { name: "Addresses", icon: HolidayVillageOutlined, type: "Input data" },
  { name: "Parcel", icon: PictureInPictureOutlined, type: "Input data" },
  { name: "Streets", icon: AddRoadOutlined, type: "Input data" },
];

interface TargetLayer {
  layerKey: string;
  displayName: string;
  fondServiceId: string;
  stageId: string;
  stageLabel: string;
}

const targetLayers: TargetLayer[] = [
  {
    layerKey: MuPowerInputLayerIds.addresses,
    displayName: "Addresses",
    fondServiceId: FondServiceLayerIds.inAddress,
    stageId: "addresses",
    stageLabel: "Importing addresses",
  },
  {
    layerKey: MuPowerInputLayerIds.parcels,
    displayName: "Parcels",
    fondServiceId: FondServiceLayerIds.inParcel,
    stageId: "parcels",
    stageLabel: "Importing parcels",
  },
  {
    layerKey: MuPowerInputLayerIds.streets,
    displayName: "Streets",
    fondServiceId: FondServiceLayerIds.inStreet,
    stageId: "streets",
    stageLabel: "Importing streets",
  },
];

type WorkflowState = "idle" | "downloading" | "importing" | "complete" | "error";

const boundaryFeatureToArea = (feature: Feature | undefined): Feature<Polygon> | null => {
  const geometry = feature?.geometry;
  if (!geometry) return null;
  if (geometry.type === "Polygon") {
    return turf.feature(geometry) as Feature<Polygon>;
  }
  if (geometry.type === "MultiPolygon") {
    const multi = geometry as MultiPolygon;
    const polygons = multi.coordinates.map((coords) => turf.polygon(coords) as Feature<Polygon>);
    if (polygons.length === 0) return null;
    if (polygons.length === 1) return polygons[0];
    const unioned = turf.union(turf.featureCollection(polygons));
    return unioned?.geometry?.type === "Polygon" ? (unioned as Feature<Polygon>) : polygons[0];
  }
  return null;
};

const MuPowerBoundaryImport: React.FC = () => {
  const dispatch = useAppDispatch();
  const versionId = useAppSelector((state) => state.project.versionId);
  const importsForVersion = useAppSelector((state) => state.imports[versionId]);
  const { clear } = useStackedNavigationContext();

  const boundaryLayerStatus = useGetImportLayer(versionId, MuPowerInputLayerIds.boundary);
  const addressesLayerStatus = useGetImportLayer(versionId, MuPowerInputLayerIds.addresses);
  const parcelsLayerStatus = useGetImportLayer(versionId, MuPowerInputLayerIds.parcels);
  const streetsLayerStatus = useGetImportLayer(versionId, MuPowerInputLayerIds.streets);

  const boundaryLayerId = boundaryLayerStatus.layer?.ID;
  const { data: boundaryFeatures } = useGetFeaturesQuery({ layerId: boundaryLayerId ?? "" }, { skip: !boundaryLayerId });

  const hasExistingData = !!addressesLayerStatus.layer || !!parcelsLayerStatus.layer || !!streetsLayerStatus.layer;

  const [workflowState, setWorkflowState] = useState<WorkflowState>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const hasStartedImportsRef = useRef(false);

  const importStageStatuses = useMemo<Record<string, StageStatus>>(() => {
    const idleFallback: StageStatus = workflowState === "importing" || workflowState === "complete" ? "Success" : "Idle";
    const statusFor = (layerKey: string): StageStatus => {
      const importEntry = importsForVersion?.[layerKey];
      if (!importEntry) return idleFallback;
      if (importEntry.status === ImportStatus.COMPLETE) return "Success";
      if (importEntry.status === ImportStatus.ERROR) return "Error";
      if (isImportInProgress(importEntry.status)) return "InProgress";
      return "Idle";
    };
    return Object.fromEntries(targetLayers.map((target) => [target.stageId, statusFor(target.layerKey)]));
  }, [importsForVersion, workflowState]);

  useEffect(() => {
    if (workflowState !== "importing" || !hasStartedImportsRef.current) return;
    const statuses = targetLayers.map((target) => importStageStatuses[target.stageId]);
    if (statuses.some((status) => status === "Error")) {
      setErrorMessage("One or more layers failed to import. Please try again.");
      setWorkflowState("error");
    } else if (statuses.every((status) => status === "Success")) {
      setWorkflowState("complete");
    }
  }, [importStageStatuses, workflowState]);

  useEffect(() => {
    if (workflowState === "complete") clear();
  }, [workflowState, clear]);

  const steps: Step[] = useMemo(() => {
    let downloadStatus: StageStatus = "Success";
    if (workflowState === "downloading") downloadStatus = "InProgress";
    else if (workflowState === "idle") downloadStatus = "Idle";
    else if (workflowState === "error" && !hasStartedImportsRef.current) downloadStatus = "Error";
    return [
      { ID: "download", Label: "Downloading base data", Status: downloadStatus },
      ...targetLayers.map<Step>((target) => ({
        ID: target.stageId,
        Label: target.stageLabel,
        Status: importStageStatuses[target.stageId] ?? "Idle",
      })),
    ];
  }, [workflowState, importStageStatuses]);

  const isInProgress = workflowState === "downloading" || workflowState === "importing";

  const proceedWithImport = useCallback(async () => {
    setShowConfirmModal(false);
    setErrorMessage(null);
    hasStartedImportsRef.current = false;
    setWorkflowState("downloading");

    const area = boundaryFeatureToArea(boundaryFeatures?.[0]);
    if (!area) {
      setErrorMessage("The uploaded boundary has no polygon geometry we can use for import.");
      setWorkflowState("error");
      return;
    }

    let response;
    try {
      response = await requestJson("POST", "/v2/area-select/features-within", {
        layers: [FondServiceLayerIds.inAddress, FondServiceLayerIds.inParcel, FondServiceLayerIds.inStreet],
        area,
      });
    } catch (error: unknown) {
      const { code, message } = error as { code?: string; message?: string };
      if (code === fondServiceErrorCodes.tooManyFeatures) {
        setErrorMessage(message ?? "The boundary is too large to import in a single pass.");
      } else {
        setErrorMessage(message ?? "There was an error downloading base data for the boundary.");
      }
      setWorkflowState("error");
      return;
    }

    try {
      setWorkflowState("importing");
      hasStartedImportsRef.current = true;
      for (const target of targetLayers) {
        const fc: FeatureCollection = response.Layers?.[target.fondServiceId] ?? turf.featureCollection([]);
        const file = new File([JSON.stringify(fc)], `${target.layerKey.replaceAll("/", "_")}.geojson`, { type: "application/geo+json" });
        await dispatch(
          addImport({
            versionId,
            layerKey: target.layerKey,
            displayName: target.displayName,
            files: [file],
          })
        );
      }
    } catch (error: unknown) {
      const { message } = error as { message?: string };
      setErrorMessage(message ?? "There was an error starting the layer imports.");
      setWorkflowState("error");
    }
  }, [boundaryFeatures, dispatch, versionId]);

  const handleOnImport = useCallback(() => {
    if (hasExistingData) {
      setShowConfirmModal(true);
    } else {
      proceedWithImport();
    }
  }, [hasExistingData, proceedWithImport]);

  const onBack = () => {
    if (!isInProgress) {
      clear();
    }
  };

  return (
    <Box>
      <StackedNavigationHeader title="Import data" onBackButtonClick={onBack} />
      <Typography fontWeight="500">Boundary</Typography>
      <ConfirmModal
        open={showConfirmModal}
        header="Replace existing data?"
        confirmText="Continue"
        cancelText="Cancel"
        color="error"
        content={
          <Typography variant="body2">
            This will replace existing imported layer data (addresses, parcels, streets). This action cannot be undone.
          </Typography>
        }
        onConfirm={proceedWithImport}
        onCancel={() => setShowConfirmModal(false)}
      />

      {workflowState === "error" && (
        <Alert severity="error" variant="compact" sx={{ my: 1 }}>
          <AlertTitle>Data import failed</AlertTitle>
          {errorMessage ?? "There was an error running data import. Please try again."}
        </Alert>
      )}

      {workflowState === "complete" && (
        <Alert severity="success" variant="compact" sx={{ my: 1 }}>
          Data import complete.
        </Alert>
      )}

      {(isInProgress || workflowState === "error") && (
        <Box sx={{ mb: 2 }}>
          <Progress steps={steps} />
        </Box>
      )}

      <Typography variant="body3" sx={{ mb: 1, display: "block" }}>
        Data that will be imported based on your boundary:
      </Typography>
      <Table size="small" variant="ag-grid-compact">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Type</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {importItems.map(({ name, icon: Icon, type, iconColor = "secondary" }) => (
            <TableRow key={name}>
              <TableCell sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Icon color={iconColor} sx={{ width: 18, height: 18 }} />
                <Box component="span" sx={{ fontWeight: "500" }}>
                  {name}
                </Box>
              </TableCell>
              <TableCell>
                <Typography variant="subtle">{type}</Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
        <Button color="primary" onClick={onBack} size="small" disabled={isInProgress}>
          Cancel
        </Button>
        <LoadingButton
          color="primary"
          variant="contained"
          onClick={handleOnImport}
          size="small"
          disabled={isInProgress || !boundaryLayerId}
          loading={isInProgress}
        >
          Import
        </LoadingButton>
      </Box>
    </Box>
  );
};

export default MuPowerBoundaryImport;
